import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import DropdownMenu from "../../../../components/DropdownMenu";
import {
  DELETE_REGION,
  DeleteRegionAction,
  DUPLICATE_REGION,
  DuplicateRegionAction,
  SET_EDITOR_MODE,
  SetEditorModeAction
} from "../../../actions/actionTypes";
import { EditorMode } from "../../../store";
import { genNewId } from "../../../util/ids";

export type RegionDropdownPlacement =
  | "bottomLeft"
  | "bottomRight"
  | "topLeft"
  | "topRight";

export interface RegionDropdownMenuProps {
  children: JSX.Element;
  regionId: string;
  laneId: string;
  /**
   * `true` — right-click on `children` opens the menu at the cursor.
   * `false` — left-click on `children` opens the menu anchored to it.
   */
  isContext: boolean;
  placement?: RegionDropdownPlacement;
  onVisibilityChange?: (visible: boolean) => void;
}

/**
 * Region context/action menu. Built on the shared <DropdownMenu/> primitive
 * so its visual language matches the rest of the app (browser TrackActions,
 * TrackInfoPanel header, etc.).
 *
 * Two trigger modes:
 *   - `isContext`: wraps children; `onContextMenu` opens menu at click coords
 *   - otherwise:   wraps children; `onClick` opens menu anchored to children
 */
export const RegionDropdownMenu: React.FC<RegionDropdownMenuProps> = ({
  children,
  regionId,
  laneId,
  isContext,
  placement = "bottomRight",
  onVisibilityChange
}) => {
  const dispatch = useDispatch();
  const triggerRef = useRef<HTMLElement | null>(null);
  const [open, setOpen] = useState(false);
  const [clickPosition, setClickPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);

  const setOpenAndNotify = useCallback(
    (visible: boolean) => {
      setOpen(visible);
      if (onVisibilityChange) {
        onVisibilityChange(visible);
      }
    },
    [onVisibilityChange]
  );

  const closeMenu = useCallback(() => {
    setOpenAndNotify(false);
    setClickPosition(null);
  }, [setOpenAndNotify]);

  // ESC closes the menu
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.keyCode === 27) {
        e.preventDefault();
        closeMenu();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, closeMenu]);

  const handleSplit = () => {
    dispatch<SetEditorModeAction>({
      type: SET_EDITOR_MODE,
      mode: EditorMode.SPLIT
    });
  };

  const handleDuplicate = () => {
    dispatch<DuplicateRegionAction>({
      type: DUPLICATE_REGION,
      regionId,
      laneId,
      newRegionId: genNewId()
    });
  };

  const handleDelete = () => {
    dispatch<DeleteRegionAction>({
      type: DELETE_REGION,
      regionId,
      laneId
    });
  };

  const items = [
    { label: "Split", icon: "split", onClick: handleSplit },
    { label: "Duplicate", icon: "copy", onClick: handleDuplicate },
    { label: "Delete", icon: "delete", onClick: handleDelete }
  ];

  // Map antd-style placement to DropdownMenu's anchor side
  const anchorSide: "left" | "right" = placement.endsWith("Left")
    ? "left"
    : "right";

  // Clone the child to attach trigger handlers + ref without an extra wrapper.
  const setTriggerRef = (node: HTMLElement | null) => {
    triggerRef.current = node;
    // Preserve any existing ref on the child element
    const childRef = ((children as unknown) as { ref?: React.Ref<HTMLElement> })
      .ref;
    if (typeof childRef === "function") childRef(node);
    else if (childRef && typeof childRef === "object") {
      (childRef as React.MutableRefObject<HTMLElement | null>).current = node;
    }
  };

  const triggerProps: Record<string, unknown> = { ref: setTriggerRef };

  if (isContext) {
    triggerProps.onContextMenu = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setClickPosition({ x: e.clientX, y: e.clientY });
      setOpenAndNotify(true);
    };
  } else {
    const baseClassName =
      (children.props as { className?: string }).className || "";
    triggerProps.className = `${baseClassName} ${open ? "active" : ""}`.trim();
    triggerProps.onClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      setOpenAndNotify(!open);
    };
  }

  return (
    <>
      {React.cloneElement(children, triggerProps)}
      <DropdownMenu
        open={open}
        onClose={closeMenu}
        items={items}
        anchor={anchorSide}
        itemAlign={anchorSide}
        portal
        anchorRef={isContext ? undefined : triggerRef}
        position={isContext ? clickPosition || undefined : undefined}
        minWidth="140px"
      />
    </>
  );
};
